<template>
  <div
    class="home"
    ref="pchome"
    :style="{
      '--hero-bg-max-height': heroBgMaxHeight,
    }"
  >
    <!-- 批量上传弹窗 -->
    <van-popup
      v-model="showImportDialog"
      round
      position="center"
      closeable
      style="width: 420px; max-width: 90vw"
      class="import-popup"
    >
      <div class="import-dialog">
        <!-- 批量上传供需信息 -->
        <h3 class="import-title">{{ $t("pcHome.bulkUploadTitle") }}</h3>
        <!-- 请上传 Excel 文件，支持 .xlsx、.xls 格式 -->
        <p class="import-desc">{{ $t("pcHome.bulkUploadDescription") }}</p>
        <van-uploader
          :after-read="handleFileUpload"
          :before-delete="beforeDeleteFile"
          v-model="importFileList"
          multiple
          :max-count="1"
          accept=".xlsx,.xls"
          :preview-size="80"
        >
          <div class="upload-trigger">
            <svg
              width="40"
              height="40"
              viewBox="0 0 48 48"
              fill="none"
              stroke="#999"
              stroke-width="1.5"
            >
              <path
                d="M28 16H12v14h4V22h12v8h4V16zM12 8v4h24V8h-4l-4 4H16l-4-4H12z"
              />
              <path
                d="M40 44H8c-2.2 0-4-1.8-4-4V8c0-2.2 1.8-4 4-4h32c2.2 0 4 1.8 4 4v32c0 2.2-1.8 4-4 4zM8 8v32h32V8H8z"
              />
            </svg>
            <!-- 点击上传文件 -->
            <span class="upload-text">{{ $t("pcHome.chooseFile") }}</span>
          </div>
        </van-uploader>
        <div class="import-actions">
          <!-- 取消 -->
          <van-button plain size="small" @click="handleCancel">{{
            $t("pcHome.cancel")
          }}</van-button>
          <!-- 确定上传 -->
          <van-button type="primary" size="small" :loading="isImportSubmitting" :disabled="isImportSubmitting" @click="submitImport">{{
            $t("pcHome.confirmUpload")
          }}</van-button>
        </div>
      </div>
    </van-popup>
    <!-- 背景图 -->
    <div class="hero-banner">
      <img :src="logoList[1].image" alt="Hero Banner" @load="updateLayout" />
      <div class="hero-search-area" role="search">
        <div class="hero-search-main">
          <img
            class="hero-search-logo"
            :src="featuredLogoSrc"
            :alt="featuredLogoAlt"
          />
          <div class="hero-search-shell">
            <!-- 搜索料号、名称 -->
            <input
              v-model.trim="heroSearchKeyword"
              class="hero-search-input"
              type="search"
              :placeholder="$t('pcHome.searchPlaceholder')"
              :aria-label="$t('pcHome.searchPlaceholder')"
              @keyup.enter="onProductSearch(heroSearchKeyword)"
            />
            <!-- 先搜一下 -->
            <button
              class="hero-search-button"
              type="button"
              @click="onProductSearch(heroSearchKeyword)"
            >
              {{ $t("pcHome.searchAction") }}
            </button>
          </div>
          <!-- 先搜服务价值 -->
          <div
            class="hero-value-strip"
            :aria-label="$t('pcHome.serviceValues')"
          >
            <div class="hero-value-item">
              <img :src="require('@/img/home/1.png')" alt="" />
              <!-- 卓越服务 -->
              <span>{{ $t("pcHome.excellentService") }}</span>
            </div>
            <div class="hero-value-item">
              <img :src="require('@/img/home/2.png')" alt="" />
              <!-- 持续创造 -->
              <span>{{ $t("pcHome.continuousInnovation") }}</span>
            </div>
            <div class="hero-value-item">
              <img :src="require('@/img/home/3.png')" alt="" />
              <!-- 协作共赢 -->
              <span>{{ $t("pcHome.winWinCooperation") }}</span>
            </div>
            <div class="hero-value-item">
              <img :src="require('@/img/home/4.png')" alt="" />
              <!-- 价值共享 -->
              <span>{{ $t("pcHome.sharedValue") }}</span>
            </div>
          </div>
        </div>
        <!-- 先搜AI -->
        <button class="hero-ai-button" type="button" @click="openAiAssistant">
          {{ $t("pcHome.ai") }}
        </button>
      </div>
    </div>

    <div class="main-container" id="pc-home-main">
      <!-- 快捷入口 -->
      <div v-if="navList && navList.length > 0" class="shortcut-wrapper">
        <div class="shortcut-container">
          <!-- 向左滑动快捷入口 -->
          <button
            v-if="shortcutScrollable"
            class="shortcut-scroll-button shortcut-scroll-button-left"
            type="button"
            :aria-label="$t('pcHome.scrollShortcutsLeft')"
            @click="scrollShortcuts(-1)"
          >
            <i class="el-icon-arrow-left"></i>
          </button>
          <div
            ref="shortcutViewport"
            class="shortcut-viewport"
            :class="{
              'shortcut-fade-left': canScrollShortcutsLeft,
              'shortcut-fade-right': canScrollShortcutsRight,
            }"
            @scroll.passive="updateShortcutScrollState"
          >
            <div class="shortcut-track">
              <div
                v-for="entry in shortcutCarouselItems"
                :key="entry.key"
                class="shortcut-item"
                @click="onJump(entry.item)"
              >
                <div class="shortcut-icon">
                  <!-- 图标 -->
                  <img :src="entry.item.imgUrl" :alt="$t('pcHome.shortcutIcon')" />
                </div>
                <div class="shortcut-text">{{ localizedShortcutLabel(entry.item) }}</div>
              </div>
            </div>
          </div>
          <!-- 向右滑动快捷入口 -->
          <button
            v-if="shortcutScrollable"
            class="shortcut-scroll-button shortcut-scroll-button-right"
            type="button"
            :aria-label="$t('pcHome.scrollShortcutsRight')"
            @click="scrollShortcuts(1)"
          >
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>
      </div>

      <!-- 商品与信息 -->
      <div class="goods-section" ref="clueSection">
        <div class="section-header" v-if="tabs.length > 0">
          <div class="tab-nav">
            <!-- 动态简中候选：由本文件选项的 titleKey 选择，只翻译标签，不改变选项值 -->
            <!-- pcHome.tradeInfo：成交信息 -->
            <!-- pcHome.supplyDemandInfo：供需商机 -->
            <div
              v-for="(tab, index) in tabs"
              :key="tab.value"
              :class="['tab-nav-item', { active: activeTab === tab.value }]"
              @click="activeTab = tab.value"
            >
              {{ $t(tab.titleKey) }}
            </div>
          </div>
          <div class="section-actions">
            <!-- 导入供需 -->
            <el-button
              v-if="activeTab === 2 && userBusinessType == 10"
              type="warning"
              size="small"
              @click="showImportDialog = true"
            >
              {{ $t("pcHome.importSupplyDemand") }}
            </el-button>
            <div class="view-toggle">
              <button
                :class="['view-btn', { active: activeListView === 'grid' }]"
                type="button"
                :aria-pressed="activeListView === 'grid'"
                @click="setActiveListView('grid')"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
                <!-- 图片 -->
                <span>{{ $t(activeTab === 0 ? "pcHome.gridView" : "pcHome.cardView") }}</span>
              </button>
              <button
                :class="['view-btn', { active: activeListView === 'table' }]"
                type="button"
                :aria-pressed="activeListView === 'table'"
                @click="setActiveListView('table')"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                  <line x1="10" y1="6" x2="10" y2="18" />
                  <line x1="14" y1="6" x2="14" y2="18" />
                </svg>
                <!-- 列表 -->
                <span>{{ $t("pcHome.listView") }}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="section-content">
          <!-- 商品标签页 -->
          <div v-if="activeTab === 0">
            <!-- 我们是有底线的 -->
            <!-- 获取数据失败，请重新加载 -->
            <van-list
              ref="goodsListContainer"
              v-model="loading"
              :finished="finished"
              :finished-text="$t('pcHome.endOfList')"
              :error-text="$t('pcHome.loadFailedRetry')"
              @load="onLoad"
            >
              <!-- 图片视图 -->
              <div v-if="viewMode === 'grid'" class="goods-grid">
                <GoodsItem
                  v-for="(item, index) in list"
                  :key="index"
                  :item="item"
                  :show-part-number="true"
                  @click="onJump(item)"
                ></GoodsItem>
              </div>

              <!-- 表格视图 -->
              <div class="table-view-container" v-else>
                <el-table
                  class="goods-table"
                  :data="list"
                  :border="false"
                  :show-overflow-tooltip="true"
                  @row-click="onJump"
                >
                  <!-- 图片 -->
                  <el-table-column
                    prop="goodsImage"
                    class-name="goods-image-cell"
                    :label="$t('pcHome.image')"
                    width="80"
                    align="center"
                  >
                    <template #default="{ row }">
                      <img
                        :src="row.goodsImage"
                        :alt="$t('pcHome.productImage')"
                        style="
                          width: 50px;
                          height: 50px;
                          object-fit: cover;
                          border-radius: 6px;
                        "
                      />
                    </template>
                  </el-table-column>
                  <!-- <el-table-column prop="goodsName" :label="$t('reportDashboard.goodsName')" min-width="200" /> -->
                  <!-- 品牌 -->
                  <el-table-column
                    prop="goodsBrand"
                    :label="$t('pcHome.brand')"
                    min-width="80"
                    align="center"
                  />
                  <!-- 料号 -->
                  <el-table-column
                    prop="goodsNum"
                    :label="$t('pcHome.partNumber')"
                    min-width="140"
                    align="center"
                  />
                  <!-- 类型 -->
                  <el-table-column
                    prop="productType"
                    :label="$t('pcClue.category')"
                    min-width="80"
                    align="center"
                  />
                  <!-- 容量 -->
                  <el-table-column
                    prop="capacity"
                    :label="$t('pcHome.capacity')"
                    min-width="80"
                    align="center"
                  />
                  <!-- 架构 -->
                  <el-table-column
                    prop="architecture"
                    :label="$t('pcHome.architecture')"
                    min-width="80"
                    align="center"
                  />
                  <!-- 速率 -->
                  <el-table-column
                    prop="speed"
                    :label="$t('pcHome.speed')"
                    min-width="80"
                    align="center"
                  />
                  <!-- 球位 -->
                  <el-table-column
                    prop="ball"
                    :label="$t('pcHome.ballCount')"
                    min-width="80"
                    align="center"
                  />
                  <el-table-column
                    prop="manufactureDate"
                    label="DC"
                    min-width="80"
                    align="center"
                  />
                  <!-- 价格 -->
                  <el-table-column
                    prop="salePrice"
                    :label="$t('pcHome.price')"
                    min-width="120"
                    align="center"
                  >
                    <template #default="{ row }">
                      <span style="font-weight: 600; color: #4286f5">
                        {{
                          row.priceUnit === 0
                            ? "CNY"
                            : row.priceUnit === 10
                              ? "USD"
                              : $t("auth.sentenceSeparator")
                        }}
                        {{ $formatPrice(row.salePrice) }}
                      </span>
                    </template>
                  </el-table-column>
                  <!-- 数量 -->
                  <el-table-column
                    prop="goodsQuantity"
                    :label="$t('pcHome.quantity')"
                    min-width="80"
                    align="center"
                  >
                    <template #default="{ row }">
                      {{ row.quantity }} {{ row.goodsUnit || $t("auth.sentenceSeparator") }}
                    </template>
                  </el-table-column>
                  <!-- 发布人 -->
                  <el-table-column :label="$t('pcHome.publisher')" width="88" align="center">
                    <template #default="{ row }">
                      <el-tooltip :content="row.shopContacts || $t('pcHome.publisher')" placement="left">
                        <span class="publisher-avatar-trigger" @click.stop>
                          <button type="button" class="publisher-chat-avatar"
                            :disabled="!row.goodsCode || !row.shopContacts"
                            :aria-label="$t('pcHome.contactPublisher', { name: row.shopContacts || $t('pcHome.publisher') })"
                            @click.stop="openGoodsPublisherConsultation(row)">
                            <span class="publisher-initial" aria-hidden="true">{{ publisherInitial({ publisherDisplayName: row.shopContacts }) }}</span>
                          </button>
                        </span>
                      </el-tooltip>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <!-- 暂无数据 -->
              <el-empty
                v-if="list && list.length <= 0"
                :description="$t('pcHome.noData')"
              />
            </van-list>
          </div>
          <!-- 信息标签页 -->
          <div v-if="activeTab !== 0 && hasClueViewPermission">
            <!-- 筛选条件 -->
            <div class="info-filter-container" ref="infoFilterContainer">
              <div class="filter-header">
                <!-- 筛选条件 -->
                <span class="filter-title">{{ $t("pcHome.filters") }}</span>
              </div>
              <div class="filter-body">
                <div class="filter-row">
                  <!-- 品牌： -->
                  <span class="filter-label">{{
                    $t("pcHome.brandLabel")
                  }}</span>
                  <div class="filter-options brand-filter-options">
                    <div class="brand-options-scroll">
                      <!-- 全部 -->
                      <span
                        class="filter-chip"
                        :class="{ active: infoFilters.brand === '' }"
                        @click="selectBrand('')"
                        >{{ $t("pcHome.all") }}</span
                      >
                      <span
                        v-for="brand in visibleBrands"
                        :key="brand.value"
                        class="filter-chip"
                        :class="{ active: infoFilters.brand === brand.text }"
                        @click="selectBrand(brand.text)"
                        >{{ brand.text }}</span
                      >
                      <!-- 搜索 -->
                      <span
                        class="filter-more"
                        @click="openBrandSearchDialog"
                        >{{ $t("pcHome.search") }}</span
                      >
                    </div>
                  </div>
                </div>
                <div class="filter-row">
                  <span class="filter-label">{{ $t("pcClue.type") }}：</span>
                  <div class="filter-options">
                    <button v-for="option in clueTypeOptions" :key="option.value" type="button"
                      class="filter-chip" :class="{ active: infoFilters.clueType === option.value }"
                      :aria-pressed="infoFilters.clueType === option.value"
                      @click="infoFilters.clueType = option.value; sekectList()">{{ option.text }}</button>
                  </div>
                </div>
                <div class="filter-row">
                  <!-- 货源地： -->
                  <span class="filter-label">{{
                    $t("pcHome.originLabel")
                  }}</span>
                  <div class="filter-options">
                    <!-- 全部 -->
                    <span
                      class="filter-chip"
                      :class="{ active: infoFilters.type === '' }"
                      @click="selectType('')"
                      >{{ $t("pcHome.all") }}</span
                    >
                    <!-- 大陆 -->
                    <span
                      class="filter-chip"
                      :class="{ active: infoFilters.type === '1' }"
                      @click="selectType('1')"
                      >{{ $t("pcHome.mainland") }}</span
                    >
                    <!-- 港澳台 -->
                    <span
                      class="filter-chip"
                      :class="{ active: infoFilters.type === '2' }"
                      @click="selectType('2')"
                      >{{ $t("pcHome.hongKongMacaoTaiwan") }}</span
                    >
                  </div>
                </div>
                <div class="filter-row">
                  <!-- 关键词： -->
                  <span class="filter-label">{{
                    $t("pcHome.keywordLabel")
                  }}</span>
                  <div class="filter-input-wrapper">
                    <!-- 搜索品牌、料号、产品类型、容量、架构、速率、球位或供应商 -->
                    <el-input
                      v-model="infoFilters.model"
                      :placeholder="$t('pcHome.filterPlaceholder')"
                      size="small"
                      class="filter-input"
                      clearable
                      @keyup.enter.native="sekectList"
                    />
                    <div class="filter-actions">
                      <!-- 重置 -->
                      <el-button size="small" @click="resetFilters">{{
                        $t("pcHome.reset")
                      }}</el-button>
                      <!-- 查询 -->
                      <el-button
                        type="primary"
                        size="small"
                        @click="sekectList"
                        >{{ $t("pcHome.query") }}</el-button
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 表格内容区域 -->
            <div
              class="table-view-container info-table-scroll information-card-container"
              ref="tableContainer"
            >
              <!-- 加载中... -->
              <div
                v-if="infoLoading && infoList.length === 0"
                class="loading-container"
                v-loading="infoLoading"
                :element-loading-text="$t('pcHome.loading')"
              ></div>
              <div v-else-if="infoError" class="error-container">
                <!-- 获取数据失败，请刷新重试 -->
                <el-empty :description="$t('pcHome.loadFailedRefresh')" />
                <!-- 重新加载 -->
                <el-button
                  type="primary"
                  size="small"
                  @click="getInfoList"
                  style="margin-top: 20px"
                >
                  {{ $t("pcHome.reload") }}
                </el-button>
              </div>
              <div v-else>
                <!-- 列表使用整行信息卡片，网格复用同一组字段和权限，避免两种模式内容不一致。 -->
                <div class="information-cards" :class="{ 'is-list': clueViewMode === 'table' }">
                  <el-checkbox
                    v-if="isAudit && infoList.length"
                    class="information-select-all"
                    :value="reviewSelection.all"
                    :indeterminate="reviewSelection.partial"
                    :disabled="Boolean(infoAction)"
                    @change="selectAllLoadedClues"
                  >{{ $t('pcHome.selectLoadedClues') }}</el-checkbox>
                  <el-empty v-if="!infoList.length" :description="$t('pcHome.noData')" />
                  <article
                    v-for="row in infoList"
                    :key="row.id"
                    class="information-card"
                    :class="{ 'is-selected': row.selected }"
                    tabindex="0"
                    @click="handleRowClick(row)"
                    @keydown.enter.self="handleRowClick(row)"
                  >
                    <div class="information-card__header">
                      <span class="clue-type" :class="{ 'is-demand': Number(row.clueType) === 2 }">{{ Number(row.clueType) === 1 ? $t('pcHome.shortcut.supply') : Number(row.clueType) === 2 ? $t('pcHome.shortcut.demand') : '--' }}</span>
                      <strong class="information-card__part">{{ row.partNumber || '--' }}</strong>
                      <strong class="information-card__brand">{{ row.goodsBrand || '--' }}</strong>
                      <div v-if="clueViewMode === 'table'" class="information-card__price"><span>{{ $t('pcHome.unitPrice') }}</span><strong>{{ row.price || '--' }}</strong></div>
                      <div v-if="isAudit" @click.stop @keydown.stop>
                        <el-checkbox v-model="row.selected" :disabled="Boolean(infoAction)" :aria-label="row.partNumber || '--'" @change="handleCustomSelect(row)" />
                      </div>
                    </div>
                    <div class="information-card__parameters">
                      <div v-for="field in informationCardFields" :key="field.prop" class="information-card__field">
                        <span>{{ $t(field.label) }}</span>
                        <strong>{{ row[field.prop] == null || row[field.prop] === '' ? '--' : row[field.prop] }}</strong>
                      </div>
                    </div>
                    <div v-if="clueViewMode === 'grid'" class="information-card__trade">
                      <div class="information-card__price"><span>{{ $t('pcHome.unitPrice') }}</span><strong>{{ row.price || '--' }}</strong></div>
                      <div class="information-card__field"><span>{{ $t('pcHome.goodsLocation') }}</span><strong>{{ row.deliveryAddress || '--' }}</strong></div>
                    </div>
                    <div class="information-card__footer">
                      <div class="information-card__field"><span>{{ $t('pcHome.batch') }}</span><strong>{{ row.manufactureDate || '--' }}</strong></div>
                      <div v-if="clueViewMode === 'table'" class="information-card__field"><span>{{ $t('pcHome.goodsLocation') }}</span><strong>{{ row.deliveryAddress || '--' }}</strong></div>
                      <div class="information-card__field"><span>{{ $t('pcHome.publishedAt') }}</span><strong>{{ row.createTime || '--' }}</strong></div>
                      <div v-if="row.supplierName" class="information-card__field"><span>{{ $t('pcHome.supplier') }}</span><strong>{{ row.supplierName }}</strong></div>
                      <div v-if="activeTab !== 2" class="information-card__publisher" @click.stop @keydown.stop>
                        <span>{{ $t('pcHome.publisher') }}</span>
                        <el-tooltip :content="row.publisherDisplayName || $t('pcHome.publisher')" placement="left">
                          <button type="button" class="publisher-chat-avatar"
                            :disabled="!row.publisherAvailable || consultationOpening || row.publisherUserId === currentConsultationUserId"
                            :aria-label="$t('pcHome.contactPublisher', { name: row.publisherDisplayName || $t('pcHome.publisher') })"
                            @click.stop="openPublisherConsultation(row)">
                            <span class="publisher-initial" aria-hidden="true">{{ publisherInitial(row) }}</span>
                          </button>
                        </el-tooltip>
                        <strong>{{ row.publisherDisplayName || '--' }}</strong>
                      </div>
                    </div>
                  </article>
                </div>


                <div
                  v-if="infoLoading && infoList.length > 0"
                  class="info-list-status"
                >
                  <!-- 加载中... -->
                  <van-loading size="20px">{{
                    $t("pcHome.loading")
                  }}</van-loading>
                </div>
                <!-- 我们是有底线的 -->
                <div
                  v-else-if="infoFinished && infoList.length > 0"
                  class="info-list-status"
                >
                  {{ $t("pcHome.endOfList") }}
                </div>
              </div>
            </div>
          </div>
          <!-- 选中项操作弹窗 -->
          <div
            v-if="activeTab !== 0 && isAudit && selectedInfoItems.length > 0"
            style="
              position: fixed;
              bottom: 20px;
              left: 50%;
              transform: translateX(-50%);
              background: #fff;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              padding: 15px 20px;
              box-shadow: 0 2px 15px rgba(0, 0, 0, 0.15);
              z-index: 1000;
              display: flex;
              justify-content: space-between;
              align-items: center;
              min-width: 300px;
              max-width: 400px;
              width: 60%;
            "
          >
            <!-- 已选择 {count} 项 -->
            <div>
              {{
                $t("pcHome.selectedCount", { count: selectedInfoItems.length })
              }}
            </div>
            <div>
              <!-- 删除 -->
              <el-button
                v-if="canDeleteSelectedClues"
                type="danger"
                :loading="infoAction === 'delete'"
                :disabled="Boolean(infoAction)"
                size="medium"
                style="margin-right: 10px"
                @click="handleDeleteSelected"
                >{{ $t("pcHome.delete") }}</el-button
              >
              <!-- 审核 -->
              <el-button
                v-if="isAudit"
                type="success"
                :loading="infoAction === 'review'"
                :disabled="Boolean(infoAction)"
                size="medium"
                @click="handleAuditSelected"
                >{{ $t("pcHome.review") }}</el-button
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 回到顶部 -->
    <el-tooltip
      v-if="isListScrolled"
      :content="$t('pcHome.backToTop')"
      placement="top"
    >
      <button type="button" class="back-top-btn" @click="scrollToListTop">
        <i class="el-icon-arrow-up"></i>
      </button>
    </el-tooltip>
    <!-- 搜索品牌 -->
    <el-dialog
      :title="$t('pcHome.searchBrand')"
      :visible.sync="showBrandSearchDialog"
      width="420px"
      :close-on-click-modal="false"
      @closed="resetBrandSearchDialog"
    >
      <div style="padding: 8px 20px 0">
        <!-- 请输入品牌名称 -->
        <el-input
          v-model.trim="brandSearchKeyword"
          :placeholder="$t('pcHome.brandNamePlaceholder')"
          clearable
          @input="handleBrandSearch"
          @keyup.enter.native="confirmBrandSearch"
        />
        <div v-if="brandSearchKeyword" class="brand-search-results">
          <!-- 搜索中... -->
          <div v-if="brandSearchLoading" class="brand-search-status">
            {{ $t("pcHome.searching") }}
          </div>
          <template v-else>
            <div
              v-for="brand in brandSearchResults"
              :key="brand.value"
              class="brand-search-item"
              :class="{
                active:
                  selectedSearchBrand &&
                  selectedSearchBrand.text === brand.text,
              }"
              @click="selectSearchBrand(brand)"
            >
              {{ brand.text }}
            </div>
            <!-- 未找到匹配的品牌 -->
            <div
              v-if="brandSearchResults.length === 0"
              class="brand-search-status"
            >
              {{ $t("pcHome.noMatchingBrands") }}
            </div>
          </template>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <!-- 取消 -->
        <el-button @click="cancelBrandSearch">{{
          $t("pcHome.cancel")
        }}</el-button>
        <!-- 确定 -->
        <el-button type="primary" @click="confirmBrandSearch">{{
          $t("pcHome.confirm")
        }}</el-button>
      </span>
    </el-dialog>
    <SupplyDemandAgentServiceChat v-if="publisherChatOpen || publisherChatSession"
      :show.sync="publisherChatOpen" :session="publisherChatSession"
      :opening="consultationOpening" :pending-publisher="pendingConsultationPublisher" />
    <OrderAgentServiceChat v-if="goodsPublisherChatOpen" :key="goodsPublisherCode"
      :show.sync="goodsPublisherChatOpen" scene-type="PRODUCT" :goods-code="goodsPublisherCode" />
    <PcFooter />
  </div>
</template>

<script>
import { Dialog, Toast } from "vant";
import GoodsItem from "@/components/pc/GoodsItem.vue";
import PcFooter from "@/components/pc/PcFooter.vue";
import { openSupplyDemandConsultation } from "@/api/agentService";
import {
  getHomeNav,
  getHomeGoods,
  getBannerAdv,
  getHomeClue,
  getClueCapabilities,
} from "@/api/home";
import { loadedClueSelectionState, setLoadedClueSelection } from "@/services/featured-review-selection";
import { getUserInfoDetail } from "@/api/user.js";
import { listPendingClue, swPendingFollowList } from "@/api/bizOppor";
import { updateFile } from "@/api/upload";
import { importClue, auditClue, reviewInformation, deleteClue } from "@/api/clue";
import { getBrandSearch } from "@/api/picker";
import { Message } from "element-ui";
import { getChatUsers } from "@/api/index";
import { shortcutLabelKeys } from "@/i18n/home/index.js";
import infiniteShortcutCarousel from "@/mixins/infiniteShortcutCarousel";
import chatMessagePrompt from "@/mixins/chatMessagePrompt";
import { resolveHomeDoodle } from "@/indexlogo/schedule";
export default {
  name: "HomePage",
  mixins: [infiniteShortcutCarousel, chatMessagePrompt],
  components: {
    GoodsItem,
    PcFooter,
    // 按需加载聊天依赖，避免路由初始化时经request/store回到尚未完成初始化的语言面板。
    SupplyDemandAgentServiceChat: () => import("@/components/agent-service/SupplyDemandAgentServiceChat.vue"),
    OrderAgentServiceChat: () => import("@/components/agent-service/OrderAgentServiceChat.vue"),
  },
  data() {
    return {
      informationCardFields: [
        { prop: "productType", label: "pcClue.category" },
        { prop: "capacity", label: "pcHome.capacity" },
        { prop: "architecture", label: "pcHome.architecture" },
        { prop: "speed", label: "pcHome.speed" },
        { prop: "ball", label: "pcHome.ballCount" },
        { prop: "goodsQuantity", label: "pcHome.quantity" },
      ],
      heroSearchKeyword: "",
      publisherChatOpen: false,
      goodsPublisherChatOpen: false,
      goodsPublisherCode: "",
      publisherChatSession: null,
      consultationOpening: false,
      consultationEpoch: 0,
      pendingConsultationPublisher: null,
      bannerList: [], //轮播
      logoList: [{}, {}], //logo图--要初始化因为我上面直接用了
      navList: [], //导航
      proList: [], //商品
      advImg: null,
      params: {
        pageSize: 30,
        pageNum: 0,
        pageNum: 1,
        which: 1,
        position: "HOME",
        status: 0,
        goodsType: null,
      },
      tabList: [
        { title: "供应", type: 1 },
        { title: "需求", type: 2 },
      ],
      list: [],
      homeGoodsRequestId: 0,
      active: 0,
      serachShow: false, //搜索查询状态刷新状态
      checkMore: true,
      refreshing: false, //刷新状态默认值
      loading: false,
      finished: false,
      error: false,
      scrollContainer: null,
      hasNewClue: null, //是否有新订单
      hasUnreadMessage: false, //是否有未读消息（谈判人）
      userBusinessType: null, // 用户业务类型
      activeTab: this.$route?.query?.tab === 'information' ? 2 : this.$route?.query?.tab === 'supply-demand' ? 1 : 0,
      infoRequestVersion: 0,
      // 视图模式：grid(图片视图) 或 table(表格视图)
      viewMode: "grid",
      clueViewMode: "table",
      // 信息列表数据
      infoList: [],
      // 信息列表加载状态
      infoLoading: false,
      // 下一页请求锁，防止停留在底部时重复触发分页请求
      infoLoadingMore: false,
      // 信息列表错误状态
      infoError: false,
      // 信息列表是否加载完成
      infoFinished: false,
      // 当前列表是否已向下滚动，用于控制回到顶部按钮
      isListScrolled: false,
      // 信息列表筛选条件
      infoFilters: {
        brand: String(this.$route?.query?.brand || ""),
        model: String(this.$route?.query?.model || ""),
        type: String(this.$route?.query?.type || ""),
        supplier: String(this.$route?.query?.supplier || ""),
        pageNum: 1,
        pageSize: 30,
        clueType: [1, 2].includes(Number(this.$route?.query?.clueType)) ? Number(this.$route.query.clueType) : 0,
      },
      brandList: [],
      showBrandSearchDialog: false,
      brandSearchKeyword: "",
      brandSearchResults: [],
      selectedSearchBrand: null,
      brandSearchLoading: false,
      brandSearchRequestId: 0,
      // 选中的信息列表项
      selectedInfoItems: [],
      infoAction: '',
      // 批量上传相关
      showImportDialog: false,
      isImportSubmitting: false,
      importFileList: [],
      isAudit: false,
      activeFeaturedDoodle: null,
      featuredDoodleTimer: null,
    };
  },
  computed: {
    activeListView() {
      return this.activeTab === 0 ? this.viewMode : this.clueViewMode;
    },
    clueTypeOptions() {
      return [
        { value: 0, text: this.$t("pcHome.all") },
        { value: 1, text: this.$t("pcHome.shortcut.supply") },
        { value: 2, text: this.$t("pcHome.shortcut.demand") },
      ];
    },
    catalogIdentity() { return this.$store.getters.token || ""; },
    currentConsultationUserId() {
      const details = this.$store.state.user?.userDetails || {};
      return String(details.userId || details.user_id || this.$store.state.user?.userInfo?.userId || '');
    },
    /** 审核组可删除已选信息；后端再次读取组权限，不能只靠按钮授权。 */
    canDeleteSelectedClues() {
      return this.isAudit && this.selectedInfoItems.length > 0;
    },
    reviewSelection() {
      return loadedClueSelectionState(this.infoList);
    },
    featuredLogoSrc() {
      return (
        this.activeFeaturedDoodle?.image ||
        this.logoList[0]?.image ||
        require("@/assets/pc/home-search-logo.png")
      );
    },
    featuredLogoAlt() {
      return this.activeFeaturedDoodle
        ? `先搜${this.activeFeaturedDoodle.name}标志`
        : "先搜";
    },
    // ...mapState({
    //   userInfo: (state) => state.user.userDetails,
    // }),
    visibleBrands() {
      return this.brandList.slice(0, 11);
    },
    heroBgMaxHeight() {
      const height = this.logoList[1]?.mark || this.logoList[1]?.remark || 0;
      return typeof height === "number" ? `${height}px` : height;
    },
    hasClueViewPermission() {
      return Boolean(this.$store.getters.token && this.currentConsultationUserId) && String(this.userBusinessType || "").split(",").some(role => role.trim() === "10");
    },
    // 标签页数据，根据用户角色动态显示
    tabs() {
      let baseTabs = [];
      // 资讯入口仅谈判人显示，与服务端权限保持一致。
      if (this.hasClueViewPermission) {
        baseTabs = [
          { titleKey: "pcHome.tradeInfo", value: 0 },
          { titleKey: "pcHome.information", value: 2 },
          { titleKey: "pcHome.supplyDemandInfo", value: 1 },
        ];
      }
      if (!baseTabs.length && this.currentConsultationUserId) {
        baseTabs = [{ titleKey: "pcHome.tradeInfo", value: 0 }];
      }
      return baseTabs;
    },
  },
  watch: {
    catalogIdentity() { this.list = []; this.queryList(); },
    '$route.query.tab'(value) {
      this.activeTab = value === 'information' ? 2 : value === 'supply-demand' ? 1 : 0;
    },
    publisherChatOpen(value) {
      if (!value && this.consultationOpening) {
        this.consultationEpoch += 1;
        this.consultationOpening = false;
      }
    },
    currentConsultationUserId() {
      this.consultationEpoch += 1;
      this.publisherChatOpen = false;
      this.publisherChatSession = null;
      this.goodsPublisherChatOpen = false;
      this.goodsPublisherCode = "";
      this.consultationOpening = false;
      this.isAudit = false;
      this.infoAction = '';
      this.selectedInfoItems = setLoadedClueSelection(this.infoList, false, false);
    },
    hasClueViewPermission(allowed) {
      if (!allowed) {
        this.infoRequestVersion += 1;
        this.infoList = [];
        this.isAudit = false;
        this.activeTab = 0;
      }
    },
    activeTab() {
      if (this.activeTab === 2 && !this.hasClueViewPermission) { this.activeTab = 0; return; }
      this.selectedInfoItems = [];
      this.infoList = [];
      if (this.activeTab !== 0) this.getInfoList();
      this.$nextTick(() => {
        this.updateListScrolled();
      });
    },
  },
  mounted() {
    this.refreshFeaturedDoodle();
    this.featuredDoodleTimer = window.setInterval(
      this.refreshFeaturedDoodle,
      10 * 1000,
    );
    this.scrollContainer = this.$refs.pchome;
    // 使用Vue的事件监听方式
    this.scrollContainer.addEventListener("scroll", this.updateLayout);
    this.scrollContainer.addEventListener("scroll", this.handleInfoScroll, {
      passive: true,
    });
    // 初始化搜索框位置
    this.updateLayout();
    // 加载品牌列表
    this.loadBrandList();
  },
  beforeDestroy() {
    window.clearInterval(this.featuredDoodleTimer);
    // 移除滚动事件监听
    if (this.scrollContainer) {
      console.log("移除滚动事件监听");
      this.scrollContainer.removeEventListener("scroll", this.updateLayout);
      this.scrollContainer.removeEventListener("scroll", this.handleInfoScroll);
    }
    this.$emit("home-nav-compact-change", false);
  },
  methods: {
    setActiveListView(mode) {
      if (this.activeTab === 0) this.viewMode = mode;
      else this.clueViewMode = mode;
    },
    localizedShortcutLabel(item) {
      const key = shortcutLabelKeys[item?.text];
      // 动态简中示例：按 home/index.js 的 shortcutLabelKeys 取快捷入口名称；完整中文见对应 zh-CN.js，示例不代表固定返回值
      // pcHome.shortcut.supplyDemand：供需
      // pcHome.shortcut.supply：供应
      // pcHome.shortcut.demand：需求
      // pcHome.shortcut.selfOperated：自营
      // pcHome.shortcut.featured：精选
      // pcHome.shortcut.bidding：竞标
      return key ? this.$t(key) : item?.text || "";
    },
    refreshFeaturedDoodle() {
      this.activeFeaturedDoodle = resolveHomeDoodle(
        new Date(),
        this.$route.query.doodle || "",
      );
    },
    // 更新顶部导航栏位置
    updateLayout() {
      const trigger = this.$el.querySelector(".shortcut-wrapper");
      const layout = document.querySelector(".layout_top");
      const pageContainer = this.scrollContainer || this.$refs.pchome;
      const triggerTop = trigger
        ? trigger.getBoundingClientRect().top
        : Number.POSITIVE_INFINITY;
      const navBottom = layout ? layout.getBoundingClientRect().bottom : 0;
      // 首屏图片异步加载前，快捷入口会短暂出现在导航栏上方。
      // 只有页面实际滚动后才允许进入紧凑态，图片加载完成后再校准一次。
      const hasScrolled = Boolean(pageContainer && pageContainer.scrollTop > 0);
      const isCompact = hasScrolled && triggerTop <= navBottom;
      this.isScroll = isCompact;
      this.updateListScrolled();
      this.$emit("home-nav-compact-change", isCompact);
    },
    // 获取当前标签页的回顶目标：成交信息列表、供需信息筛选区
    getActiveListElement() {
      if (this.activeTab !== 0) {
        return this.$refs.infoFilterContainer || null;
      }
      const goodsList = this.$refs.goodsListContainer;
      if (this.list.length === 0 || !goodsList) return null;
      return goodsList.$el || goodsList;
    },
    // 首页只要发生滚动就显示回到顶部按钮
    updateListScrolled() {
      const pageContainer = this.scrollContainer;
      this.isListScrolled = Boolean(
        pageContainer && pageContainer.scrollTop > 0,
      );
    },
    // 平滑滚动到首页最顶部
    scrollToListTop() {
      const pageContainer = this.scrollContainer;
      if (!pageContainer) return;

      pageContainer.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    // 首页滚动到底部时加载下一页供需信息
    async handleInfoScroll(event) {
      const pageContainer = event.currentTarget;
      if (
        !pageContainer ||
        this.activeTab === 0 ||
        (this.userBusinessType != 10 && this.activeTab !== 2) ||
        this.infoList.length === 0 ||
        this.infoLoading ||
        this.infoLoadingMore ||
        this.infoFinished ||
        this.infoError
      ) {
        return;
      }

      const scrollBottom =
        pageContainer.scrollHeight -
        pageContainer.scrollTop -
        pageContainer.clientHeight;
      if (scrollBottom > 1) return;

      const currentPage = this.infoFilters.pageNum;
      this.infoLoadingMore = true;
      this.infoLoading = true;
      this.infoFilters.pageNum = currentPage + 1;
      try {
        const loaded = await this.getInfoList(true);
        if (!loaded) {
          this.infoFilters.pageNum = currentPage;
        }
      } finally {
        // 必须等本次接口完全结束后，才允许下一次滚动加载
        this.infoLoadingMore = false;
      }
    },
    // 首页只上报关键词，登录判断和页面跳转统一由公共布局处理。
    onProductSearch(value) {
      this.$emit("product-search", value);
    },
    openAiAssistant() {
      this.$router.push("/pc/myAi");
    },
    isLogin() {
      return this.$store.getters.token;
    },
    async getUserInfoDetail() {
      const response = await getUserInfoDetail({ skipAuthRedirect: true, skipErrorMessage: true });
      if (response.code === 200) {
        const { businessType } = response.data.userInfo;
        this.userBusinessType = businessType; // 保存用户业务类型
        if (!this.hasClueViewPermission && Number(this.activeTab) !== 0) this.activeTab = 0;
        // 设置默认视图模式：谈判人默认表格视图，其他默认图片视图
        this.viewMode = businessType == 10 ? "table" : "grid";
        // 谈判人角色|| 商务角色
        if (businessType == 10 || businessType == 20) {
          this.needShowTip(businessType);
        }
        // 如果是谈判人，获取信息列表
        if (this.hasClueViewPermission) {
          this.getInfoList();
        }
      }
    },
    async needShowTip(type) {
      // 确保类型一致，避免 switch (===) 匹配失败
      let businessType = Number(type);
      switch (businessType) {
        case 10:
          // 谈判人-未读消息，检查 store 中的 unreadCount
          this.hasUnreadMessage = this.$store.state.user.unreadCount > 0;
          break;
        case 20:
          // 商务-新订单列表
          this.hasNewClue = await swPendingFollowList();
          break;
      }
      // 谈判人有未读消息时弹出提示
      if (businessType == 10 && this.hasUnreadMessage) {
        this.showChatMessagePrompt();
      }
      // 商务有新订单时弹出提示
      if (
        businessType == 20 &&
        this.hasNewClue.rows &&
        this.hasNewClue.rows.length
      ) {
        sessionStorage.setItem("hasTip", 1);
        Dialog.confirm({
          // 您有新订单!
          title: this.$t("pcHome.newOrders"),
          // 去接单
          confirmButtonText: this.$t("pcHome.acceptOrder"),
          confirmButtonColor: "#4689F2",
        })
          .then(() => {
            this.$router.push({
              path: "/follow/swPendingFollow",
            });
          })
          .catch(() => {
            // on cancel
          });
      }
    },
    scrollToInformationList() {
      const container = this.scrollContainer || this.$refs.pchome;
      const section = this.$refs.clueSection;
      if (!container || !section) return;
      const layout = document.querySelector('.layout_top');
      const visibleTop = Math.max(container.getBoundingClientRect().top, layout ? layout.getBoundingClientRect().bottom : 0);
      container.scrollTo({
        top: Math.max(0, container.scrollTop + section.getBoundingClientRect().top - visibleTop - 16),
        behavior: 'smooth',
      });
    },
    onJump(item) {
      if (item.text === "供需") {
        if (!this.hasClueViewPermission) { this.$router.push("/pc/featured"); return; }
        // 同页、重复路由也必须切回资讯并定位列表，不能只依赖路由监听。
        this.activeTab = 2;
        return this.$router.push({ path: "/pc/featured", query: { tab: "information" } }).catch(error => {
          if (error.name !== "NavigationDuplicated") throw error;
        }).then(() => this.$nextTick(() => this.scrollToInformationList()));
      }
      if (item.text === "百科") {
        this.$router.push("/pc/wiki");
        return;
      }
      if (item.text === "自营") {
        this.$router.push("/pc/featured");
        return;
      }
      if (item.path) {
        if (item.text == "供应" || item.text == "需求") {
          this.$router.push({
            path: item.path,
            query: { active: item.text == "供应" ? 0 : 1 },
          });
        } else {
          this.$router.push(item.path);
        }
      } else if (item.goodsCode) {
        this.$router.push(`/prodetail/${item.goodsCode}`);
      }
    },
    /** 详情和浏览器返回均保留供需标签及公开筛选；不携带账号或缓存消息。 */
    async handleRowClick(row) {
      const query = { tab: this.activeTab === 2 ? 'information' : 'supply-demand' };
      for (const key of ['clueType', 'brand', 'model', 'type', 'supplier']) {
        const value = this.infoFilters[key];
        if (value !== '' && value != null) query[key] = String(value);
      }
      await this.$router.replace({ path: '/pc/featured', query }).catch(error => {
        if (error.name !== 'NavigationDuplicated') throw error;
      });
      this.$router.push({
        path: "/pc/clue/clueDetail",
        query: { ...query, id: row.id, returnTo: 'featured' },
      });
    },
    // 处理品牌栏点击事件
    handleBrandClick(row) {
      // 阻止事件冒泡，不让品牌栏触发行点击事件
      console.log("品牌栏被点击:", row.goodsBrand);
    },
    selectBrand(brand) {
      this.infoFilters.brand = brand;
      this.infoFilters.pageNum = 1;
      this.infoList = [];
      this.getInfoList();
    },
    openBrandSearchDialog() {
      this.showBrandSearchDialog = true;
    },
    handleBrandSearch(keyword) {
      this.selectedSearchBrand = null;
      const name = keyword.trim();
      if (!name) {
        this.brandSearchResults = [];
        this.brandSearchLoading = false;
        return;
      }

      const requestId = ++this.brandSearchRequestId;
      this.brandSearchLoading = true;
      getBrandSearch({ name })
        .then((res) => {
          if (requestId !== this.brandSearchRequestId) return;
          this.brandSearchResults =
            res && res.code === 200 && Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          if (requestId === this.brandSearchRequestId) {
            this.brandSearchResults = [];
          }
        })
        .finally(() => {
          if (requestId === this.brandSearchRequestId) {
            this.brandSearchLoading = false;
          }
        });
    },
    selectSearchBrand(brand) {
      this.selectedSearchBrand = brand;
      this.brandSearchKeyword = brand.text;
    },
    confirmBrandSearch() {
      // clueList 的 brand 参数按品牌文字进行模糊查询；可直接输入，也可从联想结果回填。
      this.infoFilters.brand = this.brandSearchKeyword;
      this.infoFilters.pageNum = 1;
      this.infoList = [];
      this.showBrandSearchDialog = false;
      this.getInfoList();
    },
    cancelBrandSearch() {
      this.showBrandSearchDialog = false;
    },
    resetBrandSearchDialog() {
      this.brandSearchRequestId += 1;
      this.brandSearchKeyword = "";
      this.brandSearchResults = [];
      this.selectedSearchBrand = null;
      this.brandSearchLoading = false;
    },
    selectType(type) {
      this.infoFilters.type = type;
      this.infoFilters.pageNum = 1;
      this.infoList = [];
      this.getInfoList();
    },
    loadBrandList() {
      getBrandSearch({ name: "" })
        .then((res) => {
          this.brandList =
            res && res.code == 200 && Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          this.brandList = [];
        });
    },
    // 重置筛选条件
    resetFilters() {
      this.infoFilters = {
        brand: "",
        model: "",
        type: "",
        supplier: "",
        pageNum: 1,
        pageSize: 30,
        clueType: 0,
      };
      this.infoList = [];
      this.getInfoList();
    },
    sekectList() {
      this.infoList = [];
      this.infoFilters.pageNum = 1;
      this.getInfoList();
    },
    // 查询banner或者adv【type:1logo，2banner，3special，4adv】
    queryBannerAdv(params, callback) {
      getBannerAdv(params)
        .then((resp) => {
          callback(resp);
        })
        .catch((error) => {
          console.error("Error fetching banner data:", error);
        });
    },
    // 查询商品列表接口
    // queryList() {
    //   getHomeGoods(this.params).then((res) => {
    //     this.loading = false;
    //     if (res.code == 200 && res.data) {
    //       if (res.data.length == 0) {
    //         this.finished = true;
    //       } else {
    //         this.finished = false;
    //         this.list = this.list.concat(res.data);
    //       }
    //     } else {
    //       this.error = true;
    //     }
    //   });
    // },
    // 绘制文字图片
    drawTextCanvas(brand, name) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const width = (canvas.width = 600);
      const height = (canvas.height = 600);
      // 背景黑色
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      // 文字白色
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      // 文字区域宽度70%
      const textWidth = width * 0.7;
      // 调整字号以适应宽度
      const adjustFontSize = (text, maxWidth) => {
        let fontSize = 48;
        ctx.font = `${fontSize}px Arial`;
        while (ctx.measureText(text).width > maxWidth && fontSize > 12) {
          fontSize -= 2;
          ctx.font = `${fontSize}px Arial`;
        }
        return fontSize;
      };
      // 绘制品牌
      if (brand) {
        const brandFontSize = adjustFontSize(brand, textWidth);
        ctx.font = `${brandFontSize}px Arial`;
        ctx.fillText(brand, width / 2, height / 2 - 30);
      }
      // 绘制商品名称
      if (name) {
        const nameFontSize = adjustFontSize(name, textWidth);
        ctx.font = `${nameFontSize}px Arial`;
        ctx.fillText(name, width / 2, height / 2 + 30);
      }
      return canvas.toDataURL("image/png");
    },
    queryList() {
      const identity = this.catalogIdentity;
      const requestId = ++this.homeGoodsRequestId;
      getHomeGoods(this.params).then((res) => {
        if (requestId !== this.homeGoodsRequestId || identity !== this.catalogIdentity) return;
        this.loading = false;
        this.finished = true;
        if (res.code == 200 && res.data) {
          this.list = res.data ?? [];
          if (this.list.length == 0) return;
          this.list.forEach((item) => {
            // item.tags = "新品，热销，爆款";
            if (item.tags) {
              item.handleTags = item.tags
                .replace(/，/g, ",")
                .split(",")
                .filter((item) => item.trim() !== "");
              // 步骤1：替换中文逗号为英文逗号 → 步骤2：按英文逗号拆分 → 步骤3：过滤空字符串（避免首尾逗号问题）
            } else {
              item.handleTags = [];
            }
            // 如果没有图片，生成文字图片
            if (!item.goodsImage || item.goodsImage === "") {
              item.goodsImage = this.drawTextCanvas(
                item.goodsBrand || "",
                // 无料号
                item.goodsNum || this.$t("pcHome.noPartNumber"),
              );
            }
          });
        }
      });
    },
    //页面加载
    onLoad() {
      this.loading = false;
      // 模拟更新数据
      if (this.checkMore) {
        this.loading = true;
        // 如果有新数据
        setTimeout(() => {
          this.queryList();
        }, 300);
      } else {
        // 如果没有新数据
        this.finished = true;
      }
    },
    // onLoad() {
    //   this.loading = true;
    //   this.params.pageNum = this.params.pageNum + 1;
    //   this.queryList();
    // },

    // tabs分类
    // supeActive() {
    //   if (this.active == 0) {
    //     this.params.goodsType = 1;
    //   }
    //   if (this.active == 1) {
    //     this.params.goodsType = 2;
    //   }
    // },
    onTab(index) {
      this.active = index;
      this.params.pageNum = 1;
      this.serachShow = true;
      // this.supeActive();
      this.queryList();
    },
    // onTab(index) {
    //   this.active = index;
    //   this.serachShow = true;
    //   this.supeActive();
    //   this.onLoad();
    // },
    // 获取信息列表数据
    async getInfoList(isLoadMore = false) {
      // 只有谈判人角色才请求信息列表
      if (this.hasClueViewPermission) {
        const version = ++this.infoRequestVersion;
        const information = this.activeTab === 2;
        if (!isLoadMore) {
          // 查询失败即隐藏审核操作，不能沿用上一个账号或旧列表的权限。
          this.isAudit = false;
          this.selectedInfoItems = setLoadedClueSelection(this.infoList, false, false);
          // 首次、筛选和刷新查询始终从第一页开始
          this.infoFilters.pageNum = 1;
          this.infoLoading = true;
          this.infoError = false;
          this.infoFinished = false;
          if (this.$refs.tableContainer) {
            this.$refs.tableContainer.scrollTop = 0;
          }
        }
        try {
          if (!isLoadMore) {
            try {
              const capability = await getClueCapabilities();
              if (version !== this.infoRequestVersion) return false;
              this.isAudit = capability?.code === 200 && (information ? capability.data?.canReviewInformation : capability.data?.canReview) === true;
            } catch {
              // 权限读取失败只关闭审核操作，不阻断原有供需列表读取。
              this.isAudit = false;
            }
          }
          const response = await getHomeClue({ ...this.infoFilters, clueStatus: information ? 0 : 1 });
          if (version !== this.infoRequestVersion) return false;
          // console.log('接口返回数据:', response);
          if (response.code === 200) {
            // 处理接口返回的数据，确保字段名正确
            const processedData = response.rows.map((item) => {
              // console.log('原始数据项:', item);
              // 格式化时间
              let formattedCreateTime = "";
              if (item.createTime) {
                const date = new Date(item.createTime);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const hours = String(date.getHours()).padStart(2, "0");
                const minutes = String(date.getMinutes()).padStart(2, "0");
                const seconds = String(date.getSeconds()).padStart(2, "0");
                formattedCreateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
              }
              return {
                id: item.id || item.clueId || "",
                clueType: Number(item.clueType),
                partNumber: item.partNumber || "",
                goodsBrand: item.goodsBrand || "",
                productType: item.productType || "",
                capacity: item.capacity || "",
                architecture: item.architecture || "",
                speed: item.speed || "",
                ball: item.ball || "",
                price:
                  item.goodsPrice !== null && item.goodsPrice !== undefined
                    ? `${item.goodsPrice}${
                        item.goodsPriceUnit === 0
                          ? "CNY"
                          : item.goodsPriceUnit === 10
                            ? "USD"
                            : ""
                      }`
                    : "",
                goodsQuantity: item.goodsQuantity || "",
                manufactureDate: item.manufactureDate || "",
                deliveryAddress: item.deliveryAddress || "",
                usageScenario: item.usageScenario || "",
                supplierName: item.supplierName || "",
                supplierId: item.supplierId || "",
                publisherUserId: String(item.publisherUserId || item.clueUserId || ''),
                publisherDisplayName: item.publisherDisplayName || item.clueUserName || '',
                publisherAvatar: item.publisherAvatar || '',
                publisherAvailable: item.publisherAvailable === true,
                selected: false,
                createTime: formattedCreateTime,
              };
            });
            // console.log('处理后的数据:', processedData);
            if (isLoadMore) {
              this.infoList.push(...processedData);
            } else {
              this.infoList = processedData;
            }
            // 判断是否还有更多数据
            if (processedData.length < this.infoFilters.pageSize) {
              this.infoFinished = true;
            }
            return true;
          } else {
            this.infoError = true;
            return false;
          }
        } catch (error) {
          console.error("获取信息列表失败:", error);
          this.infoError = true;
          return false;
        } finally {
          if (version === this.infoRequestVersion) this.infoLoading = false;
        }
      }
      return false;
    },
    /** 姓名首个Unicode字符作为头像，未知姓名不伪造姓氏。 */
    publisherInitial(row) {
      return Array.from(String(row.publisherDisplayName || '').trim())[0] || '?';
    },
    /** 成交商品复用详情页的商品咨询，由服务端从商品解析真实联系人。 */
    openGoodsPublisherConsultation(row) {
      if (!row.goodsCode || !row.shopContacts) return;
      if (!this.currentConsultationUserId) {
        window.dispatchEvent(new Event("open-login-dialog"));
        return;
      }
      this.goodsPublisherCode = String(row.goodsCode);
      this.goodsPublisherChatOpen = true;
    },
    /** 每次进入使用独立幂等ID；迟到响应不得覆盖换账号或后续点击的货物。 */
    async openPublisherConsultation(row) {
      if (!row.publisherAvailable || this.consultationOpening) return;
      const epoch = ++this.consultationEpoch;
      this.consultationOpening = true;
      this.pendingConsultationPublisher = { userId: row.publisherUserId, name: row.publisherDisplayName };
      this.publisherChatOpen = true;
      try {
        const entryId = globalThis.crypto?.randomUUID?.() || `entry_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const response = await openSupplyDemandConsultation({ clueId: String(row.id), entryId });
        if (epoch !== this.consultationEpoch) return;
        if (response.code !== 200 || !response.data?.conversation || !response.data?.context)
          // 发布人咨询暂时无法打开，请重试
          throw new Error(response.msg || this.$t('pcHome.publisherConsultationFailed'));
        this.publisherChatSession = response.data;
        this.publisherChatOpen = true;
      } catch (error) {
        if (epoch === this.consultationEpoch) {
          this.publisherChatOpen = false;
          // 发布人咨询暂时无法打开，请重试
          Message.error(error.message || this.$t('pcHome.publisherConsultationFailed'));
        }
      } finally {
        if (epoch === this.consultationEpoch) this.consultationOpening = false;
      }
    },
    // 处理文件上传
    async handleFileUpload(file) {
      file.status = "uploading";
      // 上传中...
      file.message = this.$t("pcHome.uploading");
      const formData = new FormData();
      formData.append("file", file.file);
      try {
        const resp = await updateFile(formData);
        if (resp.code == 200) {
          file.status = "success";
          // 上传成功
          file.message = this.$t("pcHome.uploadSuccess");
          file.url = resp.url;
        } else {
          // 上传失败
          Message({ message: this.$t("pcHome.uploadFailed"), type: "error" });
          file.status = "failed";
          // 上传失败
          file.message = this.$t("pcHome.uploadFailed");
        }
      } catch (err) {
        // 上传失败
        Message({ message: this.$t("pcHome.uploadFailed"), type: "error" });
        file.status = "failed";
        // 上传失败
        file.message = this.$t("pcHome.uploadFailed");
      }
    },
    // 删除文件
    beforeDeleteFile() {
      return true;
    },
    // 提交导入
    submitImport() {
      if (this.isImportSubmitting) return;
      if (this.importFileList.length === 0) {
        // 请选择要上传的文件
        Message.warning(this.$t("pcHome.chooseFileFirst"));
        return;
      }

      const fileItem = this.importFileList[0];
      if (fileItem.status !== "success") {
        // 文件上传中，请等待上传完成
        Message.warning(this.$t("pcHome.waitForUpload"));
        return;
      }

      const formData = new FormData();
      formData.append("file", fileItem.file);

      this.isImportSubmitting = true;
      return importClue(formData).then((res) => {
        if (res.code == 200) {
          // 导入成功
          Toast.success(this.$t("pcHome.importSuccess"));
          this.showImportDialog = false;
          this.importFileList = [];
          // 导入结果进入资讯待审列表。
          if (this.activeTab === 2) this.getInfoList();
          else this.activeTab = 2;
        } else {
          // 导入失败
          Toast.error(res.msg || this.$t("pcHome.importFailed"));
        }
      }).catch((error) => {
        console.error("供需信息导入失败", error);
      }).finally(() => {
        this.isImportSubmitting = false;
      });
    },
    // 处理取消操作
    handleCancel() {
      this.showImportDialog = false;
      this.importFileList = [];
    },
    // 处理信息列表项选择
    handleCustomSelect(row) {
      if (!this.isAudit) {
        this.selectedInfoItems = setLoadedClueSelection(this.infoList, false, false);
        return;
      }
      // 单选、多选和全选始终从当前行状态派生，避免重复change导致同一ID重复提交。
      this.selectedInfoItems = this.infoList.filter(item => item.selected);
    },
    /** 表头选择只覆盖已加载项；取消时同时清理批量操作集合。 */
    selectAllLoadedClues(selected) {
      this.selectedInfoItems = setLoadedClueSelection(this.infoList, this.isAudit, selected);
    },
    /** 删除和审核共用完整的确认/失败处理；单选、多选使用同一接口。 */
    handleDeleteSelected() {
      return this.performClueSelectionAction('delete');
    },
    handleAuditSelected() {
      return this.performClueSelectionAction('review');
    },
    /** 固定确认时的ID和操作者；失败保留选择，换号后迟到结果不得覆盖新账号。 */
    async performClueSelectionAction(action) {
      if (!this.isAudit || this.infoAction || (action === 'delete' && !this.canDeleteSelectedClues)) return;
      const ids = [...new Set(this.selectedInfoItems.map(item => item.id))];
      if (!ids.length) return;
      const actor = this.currentConsultationUserId;
      const information = this.activeTab === 2;
      // 动态简中来源：删除确认/是否删除这{count}条数据？/删除成功；审核确认/是否审核这{count}条数据？/审核成功。
      const labels = action === 'delete'
        ? { title: 'pcHome.deleteConfirmTitle', message: 'pcHome.deleteConfirmMessage', success: 'pcHome.deleteSuccess' }
        : { title: 'pcHome.reviewConfirmTitle', message: 'pcHome.reviewConfirmMessage', success: 'pcHome.reviewSuccess' };
      this.infoAction = action;
      try {
        try {
          // 动态简中候选：由本文件选项的 title 选择，只翻译标签，不改变选项值
          // pcHome.deleteConfirmTitle：删除确认
          // pcHome.reviewConfirmTitle：审核确认
          // 动态简中候选：由本文件选项的 message 选择，只翻译标签，不改变选项值
          // pcHome.deleteConfirmMessage：是否删除这{count}条数据？
          // pcHome.reviewConfirmMessage：是否审核这{count}条数据？
          await Dialog.confirm({ title: this.$t(labels.title), message: this.$t(labels.message, { count: ids.length }),
            // 确定 / 取消
            // 确定
            // 取消
            confirmButtonText: this.$t('pcHome.confirm'), cancelButtonText: this.$t('pcHome.cancel') });
        } catch { return; }
        if (!this.isAudit || actor !== this.currentConsultationUserId || information !== (this.activeTab === 2)) return;
        const response = await (action === 'delete' ? deleteClue : information ? reviewInformation : auditClue)({ ids });
        if (actor !== this.currentConsultationUserId) return;
        // 操作失败
        if (response?.code !== 200) throw new Error(response?.msg || this.$t('common.operationFailed'));
        // 动态简中候选：由本文件选项的 success 选择，只翻译标签，不改变选项值
        // pcHome.deleteSuccess：删除成功
        // pcHome.reviewSuccess：审核成功
        Message.success(this.$t(labels.success));
        this.infoList.forEach(item => { item.selected = false; });
        this.selectedInfoItems = [];
        await this.getInfoList();
        if (action === 'review') this.queryList();
      } catch (error) {
        // 操作失败：不能将接口失败当作“取消确认”吞掉。
        // 操作失败
        if (actor === this.currentConsultationUserId) Message.error(error.message || this.$t('common.operationFailed'));
      } finally {
        if (actor === this.currentConsultationUserId) this.infoAction = '';
      }
    },
  },
  async created() {
    // 检查用户登录状态，获取用户信息
    if (this.isLogin()) {
      this.getUserInfoDetail().catch(() => {});
    }
    this.queryBannerAdv(
      { which: 1, type: 1, position: "HOME", status: 0 },
      (res) => {
        if (res.code == 200 && res.data && res.data.length > 0) {
          this.logoList = res.data.sort((a, b) => a.sort - b.sort);
        }
      },
    );
    this.queryBannerAdv(
      { which: 1, type: 2, position: "HOME", status: 0 },
      (res) => {
        if (res.code == 200 && res.data && res.data.length > 0) {
          this.bannerList = res.data;
        }
      },
    );
    this.queryBannerAdv(
      { which: 1, type: 4, position: "HOME", status: 0 },
      (res) => {
        if (res.code == 200 && res.data && res.data.length > 0) {
          this.advImg = res.data[0].image;
        }
      },
    );
    // 加载商品列表
    this.onLoad();
    getHomeNav({ pageId: 0, which: 1, position: "HOME", status: 0 }).then(
      (resp) => {
        this.navList = resp.data;
        this.$nextTick(() => {
          this.updateShortcutScrollState();
          this.updateLayout();
        });
      },
    );
  },
};
</script>

<style lang="less" scoped>
// 48px选择列去掉表格默认溢出省略，防止复选框被挤出“..”。
:deep(.clue-selection-column .cell),
:deep(.clue-selection-header .cell) {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: visible;
  text-overflow: clip;
  .el-checkbox__label { display: none; }
}
.publisher-avatar-trigger { display: inline-flex; }
.publisher-chat-avatar {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: transform 200ms cubic-bezier(.2,.8,.2,1), background-color 200ms ease;
  &:hover:not(:disabled) { transform: scale(1.08); background-color: #f2f7ff; }
  &:focus-visible { outline: 2px solid #4286f5; outline-offset: 2px; }
  &:disabled { cursor: default; opacity: .5; }
  .publisher-initial {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #027a48;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
  }
  @media (prefers-reduced-motion: reduce) { transition: none; &:hover:not(:disabled) { transform: none; } }
}
.home {
  --home-content-shift: clamp(0px, calc(12.5vw - 160px), 80px);
  width: 100%;
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: #f4f4f6;
}

.back-top-btn {
  position: fixed;
  right: 96px;
  bottom: 96px;
  z-index: 20;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(66, 134, 245, 0.18);
  border-radius: 50%;
  background: #4286f5;
  color: #fff;
  box-shadow: 0 10px 22px rgba(66, 134, 245, 0.24);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition:
    background-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background: #2f74e6;
    transform: translateY(-2px);
  }
}

.hero-banner {
  width: 100%;
  position: relative;
  margin-top: -180px;
  overflow: hidden;

  > img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: cover;
    transform: translateY(180px);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
    height: 180px;
    background: linear-gradient(
      to bottom,
      rgba(244, 244, 246, 0) 0%,
      rgba(244, 244, 246, 0.72) 68%,
      #f4f4f6 100%
    );
    pointer-events: none;
  }

  .hero-search-area {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 14px;
    width: min(820px, calc(100% - 80px));
    transform: translate(-50%, calc(-50% + 50px - var(--home-content-shift)));
  }

  .hero-search-main {
    position: relative;
    display: flex;
    flex: 1;
    min-width: 0;
  }

  .hero-search-logo {
    position: absolute;
    bottom: calc(100% + 36px);
    left: 50%;
    display: block;
    width: 430px;
    max-width: 70%;
    height: auto;
    object-fit: contain;
    transform: translateX(-50%);
  }

  .hero-search-shell {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    height: 56px;
    padding: 5px 6px 5px 24px;
    border: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 30px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 6px 16px rgba(32, 65, 116, 0.08);
    box-sizing: border-box;
    backdrop-filter: blur(8px);
  }

  .hero-value-strip {
    position: absolute;
    top: calc(100% + 12px);
    left: 50%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 88%;
    color: #69717d;
    transform: translateX(-50%);
  }

  .hero-value-item {
    display: flex;
    flex: 0 0 auto;
    min-width: 0;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 13px;
    line-height: 20px;
    white-space: nowrap;

    img {
      flex: 0 0 18px;
      width: 18px;
      height: 18px;
      object-fit: contain;
    }
  }

  .hero-search-input {
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0 16px 0 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: #25324a;
    font-size: 16px;

    &::placeholder {
      color: #9aa4b5;
    }

    &::-webkit-search-cancel-button {
      cursor: pointer;
    }
  }

  .hero-search-button,
  .hero-ai-button {
    height: 46px;
    border-radius: 24px;
    font-size: 15px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition:
      transform 0.2s ease,
      background-color 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-1px);
    }

    &:focus-visible {
      outline: 3px solid rgba(66, 134, 245, 0.24);
      outline-offset: 2px;
    }
  }

  .hero-search-button {
    flex: 0 0 118px;
    border: 0;
    background: #4286f5;
    color: #ffffff;

    &:hover {
      background: #3277e8;
      box-shadow: 0 8px 18px rgba(66, 134, 245, 0.24);
    }
  }

  .hero-ai-button {
    flex: 0 0 106px;
    border: 1px solid rgba(66, 134, 245, 0.24);
    background: rgba(255, 255, 255, 0.94);
    color: #357ae8;
    box-shadow: 0 12px 28px rgba(32, 65, 116, 0.12);

    &:hover {
      background: #ffffff;
      box-shadow: 0 12px 28px rgba(66, 134, 245, 0.18);
    }
  }
}

// 下列内容的整体容器
.main-container {
  width: @pc-container-width;
  position: relative;
  z-index: 3;
  margin: calc(-278px - var(--home-content-shift)) auto 0;
  display: flex;
  flex-direction: column;
  padding: 0 0 40px;

  @media (max-width: 1400px) {
    width: calc(100% - 200px);
  }
}

@media (max-width: 700px) {
  .home {
    --home-content-shift: 0px;
    overflow-x: hidden;
  }

  .hero-banner {
    min-height: 360px;
    margin-top: 0;

    > img {
      width: auto;
      min-width: 100%;
      height: 360px;
      transform: none;
    }

    .hero-search-area {
      top: 48%;
      flex-direction: column;
      gap: 10px;
      width: calc(100% - 28px);
      transform: translate(-50%, -50%);
    }

    .hero-search-main {
      width: 100%;
    }

    .hero-search-logo {
      bottom: calc(100% + 24px);
      width: 72%;
      max-width: 280px;
    }

    .hero-search-shell {
      height: 50px;
      padding-left: 15px;
    }

    .hero-search-input {
      padding-right: 8px;
      font-size: 13px;
    }

    .hero-search-button {
      flex-basis: 88px;
      height: 40px;
      font-size: 12px;
    }

    .hero-ai-button {
      width: 104px;
      height: 38px;
      flex: 0 0 38px;
      font-size: 12px;
    }

    .hero-value-strip {
      top: calc(100% + 9px);
      width: 96%;
      gap: 5px;
    }

    .hero-value-item {
      gap: 2px;
      font-size: 10px;
    }

    .hero-value-item img {
      width: 13px;
      height: 13px;
      flex-basis: 13px;
    }
  }

  .main-container {
    width: calc(100% - 24px);
    margin: -28px auto 0;
    padding-bottom: 22px;
  }

  .shortcut-wrapper {
    margin-bottom: 14px;
  }
}

.shortcut-wrapper {
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 24px;
}

.import-dialog {
  padding: 28px 32px 32px;
  text-align: center;

  .import-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0 0 8px 0;
  }

  .import-desc {
    font-size: 13px;
    color: #999;
    margin: 0 0 24px 0;
  }

  .upload-trigger {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 24px;
    background: #f8fafc;
    border: 2px dashed #e2e8f0;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.25s ease;

    &:hover {
      border-color: #4286f5;
      background: #f0f7ff;

      svg {
        stroke: #4286f5;
      }
    }

    .upload-text {
      margin-top: 12px;
      font-size: 13px;
      color: #666;
    }
  }

  .import-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 24px;
  }
}

:deep(.van-popup--center) {
  background: #ffffff;
  border-radius: 16px;
  position: fixed !important;
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
  right: auto !important;
  bottom: auto !important;
  margin: 0 !important;
}

:deep(.van-popup__close-icon) {
  color: #999;
}

.shortcut-container {
  position: relative;
  padding: 0;
}

.shortcut-viewport {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-width: none;
  overscroll-behavior-inline: contain;

  &::-webkit-scrollbar {
    display: none;
  }

  &.shortcut-fade-left {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0,
      #000 48px,
      #000 100%
    );
    mask-image: linear-gradient(to right, transparent 0, #000 48px, #000 100%);
  }

  &.shortcut-fade-right {
    -webkit-mask-image: linear-gradient(
      to right,
      #000 0,
      #000 calc(100% - 48px),
      transparent 100%
    );
    mask-image: linear-gradient(
      to right,
      #000 0,
      #000 calc(100% - 48px),
      transparent 100%
    );
  }

  &.shortcut-fade-left.shortcut-fade-right {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0,
      #000 48px,
      #000 calc(100% - 48px),
      transparent 100%
    );
    mask-image: linear-gradient(
      to right,
      transparent 0,
      #000 48px,
      #000 calc(100% - 48px),
      transparent 100%
    );
  }
}

.shortcut-track {
  display: flex;
  gap: 12px;
  width: max-content;
  padding: 10px 0 16px;
}

.shortcut-item {
  flex: 0 0 150px;
  width: 150px;
  height: 136px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 1px solid rgba(225, 230, 238, 0.78);
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(32, 65, 116, 0.035);
  cursor: pointer;
  padding: 16px 12px;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    border-color: rgba(66, 134, 245, 0.28);
    box-shadow: 0 8px 18px rgba(32, 65, 116, 0.08);
    transform: translateY(-2px);

    .shortcut-text {
      color: #4689f2;
    }
  }
}

.shortcut-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;

  img {
    width: 50px;
    height: 50px;
    object-fit: contain;
  }
}

.shortcut-text {
  font-size: 15px;
  color: #4f5663;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.shortcut-scroll-button {
  position: absolute;
  top: 50%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(75, 82, 94, 0.58);
  color: #ffffff;
  box-shadow: 0 3px 10px rgba(32, 39, 52, 0.1);
  cursor: pointer;
  transform: translateY(-50%);
  transition:
    opacity 0.2s ease,
    background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #4286f5;
  }

  &:disabled {
    opacity: 0.28;
    cursor: default;
  }

  &.shortcut-scroll-button-left {
    left: -44px;
  }

  &.shortcut-scroll-button-right {
    right: 8px;
  }
}

.goods-section {
  margin-top: 12px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tab-nav {
  display: flex;
  gap: 4px;
  background: #f5f7fa;
  padding: 4px;
  border-radius: 10px;
}

.tab-nav-item {
  padding: 10px 24px;
  font-size: 15px;
  cursor: pointer;
  border-radius: 8px;
  color: #666;
  font-weight: 500;
  transition: all 0.25s ease;

  &:hover {
    color: #4689f2;
    background: rgba(70, 137, 242, 0.06);
  }

  &.active {
    color: #ffffff;
    font-weight: 600;
    background: linear-gradient(135deg, #4689f2 0%, #357ae8 100%);
    box-shadow: 0 2px 8px rgba(70, 137, 242, 0.3);
  }
}

.view-toggle {
  display: flex;
  gap: 4px;
  background: #f5f7fa;
  padding: 4px;
  border-radius: 10px;
}

.view-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  color: #999;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    color: #666;
    background: rgba(0, 0, 0, 0.04);
  }

  &.active {
    color: #333;
    background: #ffffff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
}

.section-content {
  min-height: 400px;
}

// 商品列表样式
.goods-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.table-view-container {
  margin-top: 20px;
  background: #ffffff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
  overflow-x: hidden;
}

.goods-table {
  :deep(.el-table__row) {
    cursor: pointer;
  }

  :deep(.goods-image-cell) {
    .cell {
      padding: 0;
      text-overflow: clip;
    }

    img {
      display: block;
      margin: 0 auto;
    }
  }
}

.info-table-scroll {
  box-sizing: border-box;
  height: auto;
  overflow-y: visible;

  .clue-type {
    display: inline-block;
    padding: 3px 6px;
    border-radius: 5px;
    color: #2f7cf6;
    background: #edf5ff;
    font-size: 12px;
    line-height: 20px;

    &.is-demand {
      color: #b76816;
      background: #fff5e6;
    }
  }
}

.information-card-container {
  padding: 0;
  background: transparent;
  box-shadow: none;
  overflow: visible;

  .information-cards {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;

    &.is-list {
      grid-template-columns: minmax(0, 1fr);
      gap: 12px;

      .information-card {
        padding: 18px 22px;

        &__header { gap: 16px; padding-bottom: 14px; border-bottom: 1px solid #edf0f5; }
        &__part { order: 0; flex: 1; }
        &__brand { max-width: 25%; padding: 0; background: transparent; color: #1f2937; font-size: 16px; }
        &__price { margin-left: auto; white-space: nowrap; }
        &__parameters { grid-template-columns: repeat(6, minmax(0, 1fr)); }
        &__footer { gap: 8px 32px; }

        @media (max-width: 1100px) {
          &__parameters { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
      }
    }

    @media (max-width: 1200px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    @media (max-width: 760px) { grid-template-columns: minmax(0, 1fr); }
  }

  .information-select-all {
    grid-column: 1 / -1;
    justify-self: start;
    padding: 4px 0;
  }

  .information-card {
    min-width: 0;
    padding: 20px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #ffffff;
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s;

    &:hover,
    &:focus-visible,
    &.is-selected {
      border-color: #99bdfa;
      background: #f8fbff;
    }

    &__header {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;

      > div:last-child { margin-left: auto; }
    }

    &__part,
    &__brand {
      color: #1f2937;
      font-size: 16px;
      font-weight: 600;
      overflow-wrap: anywhere;
    }

    &__part {
      order: 1;
      flex: 1 0 100%;
      min-width: 0;
      line-height: 24px;
    }

    &__brand {
      max-width: 65%;
      padding: 2px 8px;
      border-radius: 4px;
      color: #4286f5;
      background: #ecf5ff;
      font-size: 12px;
      line-height: 20px;
    }

    &__price {
      display: flex;
      align-items: baseline;
      gap: 8px;
      flex-wrap: wrap;

      span { color: #667085; font-size: 12px; }
      strong { color: #4286f5; font-size: 18px; font-weight: 600; }
    }

    &__parameters {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px 18px;
      padding: 16px 0;
    }

    &__field {
      display: flex;
      align-items: baseline;
      gap: 8px;
      min-width: 0;
      font-size: 13px;
      line-height: 22px;

      span { flex-shrink: 0; color: #667085; white-space: nowrap; }
      strong { min-width: 0; color: #344054; font-weight: 500; overflow-wrap: anywhere; }
    }

    &__publisher {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;
      color: #667085;
      font-size: 13px;

      strong { color: #344054; font-weight: 500; }
    }

    &__trade {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px 16px;
      padding-bottom: 16px;
    }

    &__footer {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 16px;
      padding-top: 12px;
      border-top: 1px solid #f0f2f5;
    }


  }
}

.info-list-status {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  color: #969799;
  font-size: 13px;
}

:deep(.el-table) {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: none;

  .el-table__header-wrapper {
    th {
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
      font-weight: 600;
      color: #475569;
      font-size: 13px;
      text-align: center;
      padding: 14px 8px;
      border-bottom: 2px solid #e2e8f0;

      .cell {
        white-space: nowrap;
      }
    }
  }

  .el-table__body-wrapper {
    td {
      font-size: 13px;
      color: #334155;
      padding: 14px 8px;
      text-align: center;
      border-bottom: 1px solid #f1f5f9;
      vertical-align: middle;

      .cell {
        white-space: normal;
        word-break: break-all;
      }
    }
  }

  .el-table__row:nth-child(even) {
    background-color: #fafbfc;
  }

  .el-table__row:hover {
    background-color: #eff6ff;
  }

  .el-table__body tr:hover > td {
    background-color: #eff6ff;
  }
}

.info-filter-container {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid #f5f7fa;

  .filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f2f5;

    .filter-title {
      font-size: 15px;
      font-weight: 600;
      color: #1a1a1a;
      letter-spacing: 0.3px;
    }
  }

  .filter-body {
    .filter-row {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 8px 0;
    }

    .filter-item {
      display: flex;
      align-items: center;
      gap: 10px;

      &.filter-item-wide {
        flex: 1;
        min-width: 200px;
      }
    }

    .filter-label {
      font-size: 13px;
      color: #666666;
      white-space: nowrap;
      width: 56px;
      flex-shrink: 0;
      font-weight: 500;
      letter-spacing: 0.2px;
      padding-top: 6px;
    }

    .filter-input {
      width: 150px;

      &:deep(.el-input__wrapper) {
        border-radius: 8px;
        border-color: #e8ecf0;
        box-shadow: none;
        transition: all 0.25s ease;

        &:hover {
          border-color: #cdd5de;
        }

        &.is-focus {
          border-color: #4286f5;
          box-shadow: 0 0 0 3px rgba(66, 134, 245, 0.1);
        }
      }

      &:deep(.el-input__inner) {
        font-size: 13px;
        color: #333;
        height: 32px;
        line-height: 32px;
      }

      &:deep(.el-select__wrapper) {
        border-radius: 8px;
        border-color: #e8ecf0;
        box-shadow: none;
        transition: all 0.25s ease;

        &:hover {
          border-color: #cdd5de;
        }

        &.is-focus {
          border-color: #4286f5;
          box-shadow: 0 0 0 3px rgba(66, 134, 245, 0.1);
        }
      }

      &:deep(.el-select__placeholder) {
        font-size: 13px;
        color: #999;
      }

      &:deep(.el-select__input) {
        font-size: 13px;
        color: #333;
      }

      &:deep(.el-input__clear) {
        color: #c0c4cc;
        font-size: 14px;

        &:hover {
          color: #666;
        }
      }
    }

    .filter-actions {
      display: flex;
      gap: 10px;

      :deep(.el-button) {
        font-size: 13px;
        padding: 6px 16px;
        border-radius: 8px;
        font-weight: 500;
        transition: all 0.25s ease;

        &.el-button--primary {
          background: linear-gradient(135deg, #4286f5 0%, #357ae8 100%);
          border: none;
          color: #ffffff;
          box-shadow: 0 2px 8px rgba(66, 134, 245, 0.3);

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(66, 134, 245, 0.4);
          }
        }

        &:not(.el-button--primary) {
          background: #f5f7fa;
          border-color: #e8ecf0;
          color: #666;

          &:hover {
            background: #edf1f5;
            border-color: #dce3ea;
          }
        }
      }
    }
  }
}

// 加载和错误状态样式
.loading-container,
.error-container {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.footer {
  margin-top: 60px;

  .footer-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, #e0e0e0, transparent);
    margin-bottom: 40px;
  }

  .footer-content {
    width: @pc-container-width;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    padding-bottom: 40px;

    .footer-left {
      flex: 1;

      .footer-logo {
        margin-bottom: 20px;
      }

      .social-icons {
        display: flex;
        gap: 20px;
        margin-bottom: 20px;

        .social-icon {
          width: 40px;
          height: 40px;
          cursor: pointer;
          opacity: 0.7;
          transition: opacity 0.3s ease;

          &:hover {
            opacity: 1;
          }

          img {
            width: 100%;
            height: 100%;
          }
        }
      }

      .copyright {
        font-size: 14px;
        color: #999;
        margin-bottom: 8px;
      }

      .icp {
        font-size: 13px;
        color: #bbb;

        a {
          color: #bbb;
          text-decoration: none;

          &:hover {
            color: #4689f2;
          }
        }
      }
    }

    .footer-right {
      display: flex;
      gap: 60px;

      .footer-column {
        .column-title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin-bottom: 15px;
        }

        .column-link {
          display: block;
          font-size: 14px;
          color: #666;
          margin-bottom: 10px;
          text-decoration: none;
          transition: color 0.3s ease;

          &:hover {
            color: #4689f2;
          }
        }
      }
    }
  }
}

.filter-options {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
}

.brand-filter-options {
  flex-wrap: nowrap;
  overflow: hidden;
}

.brand-options-scroll {
  display: flex;
  flex: 1;
  min-width: 0;
  gap: 8px 10px;
  overflow: hidden;
}

.brand-options-scroll .filter-chip,
.brand-filter-options .filter-more {
  flex: 0 0 auto;
}

.brand-filter-options .filter-more {
  padding-left: 12px;
  background: #fff;
}

.filter-chip,
.filter-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  font-size: 13px;
  color: #606266;
  padding: 0 11px;
  border-radius: 4px;
  cursor: pointer;
  line-height: 1;
  white-space: nowrap;
}

button.filter-chip {
  border: 0;
  background: transparent;
  font-family: inherit;
}

.filter-chip:hover,
.filter-more {
  color: #1989fa;
}

.filter-chip.active {
  color: #1989fa;
  background: #e8f4ff;
}

.brand-search-results {
  max-height: 120px;
  margin-top: 0;
  overflow-y: auto;
  border: 1px solid #dcdfe6;
  border-top: 0;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  position: relative;
  z-index: 1;
}

.brand-search-item,
.brand-search-status {
  padding: 10px 12px;
  font-size: 14px;
  color: #606266;
}

.brand-search-item {
  cursor: pointer;
}

.brand-search-item:hover,
.brand-search-item.active {
  color: #1989fa;
  background: #ecf5ff;
}

.brand-search-status {
  color: #909399;
  text-align: center;
}

.filter-input-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-input-wrapper .filter-input {
  flex: 0 0 40%;
  max-width: 40%;
}
</style>
